import { type ParsedUrlQuery } from "querystring"
import { type WizardSteps } from "@krakentech/blueprint-onboarding/next-wizard/pages-router"
import { AffiliateCode } from "@/constants/affiliateCode"
import { isDualEnergy, isGasOnly } from "@/features/subscription"
import { navigatorRoutesPaths } from "@/routes/navigator"
import { OnboardingRouteNames } from "@/routes/onboarding"
import { nestedQueryParams } from "@/utils/nestedQueryParams"
import { type OnboardingFormValues } from "../types/onboardingFormValues"

function getOnboardingQueryParams(formValues: OnboardingFormValues) {
    const queryParams: ParsedUrlQuery = {}

    if (isGasOnly(formValues.selectedEnergyTypes)) {
        queryParams.produit = "gaz"
    } else if (isDualEnergy(formValues.selectedEnergyTypes)) {
        queryParams.produit = "dual"
    }

    if (formValues.affiliateCode) {
        queryParams.affiliate = formValues.affiliateCode
    }

    if (formValues.affiliateQueryParams) {
        queryParams.affiliateQueryParams = nestedQueryParams.stringify(
            formValues.affiliateQueryParams
        )
    }

    if (formValues.electricity.supplyPoint.meterPoint.id) {
        queryParams.electricityMeterPointId = formValues.electricity.supplyPoint.meterPoint.id
    }

    if (formValues.gas.supplyPoint.meterPoint.id) {
        queryParams.gasMeterPointId = formValues.gas.supplyPoint.meterPoint.id
    }

    return queryParams
}

export function getNavigatorSteps(formValues: OnboardingFormValues) {
    const query = getOnboardingQueryParams(formValues)

    const sharedSteps = [
        {
            name: OnboardingRouteNames.ElectricityMeterIneligible,
            pathname: navigatorRoutesPaths.electricityMeterIneligible.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.GasMeterIneligible,
            pathname: navigatorRoutesPaths.gasMeterIneligible.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.AddressIneligible,
            pathname: navigatorRoutesPaths.addressIneligible.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.Quote,
            pathname: navigatorRoutesPaths.quote.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.MeterReadings,
            pathname: navigatorRoutesPaths.meterReadings.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.PersonalDetails,
            pathname: navigatorRoutesPaths.personalDetails.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.PaymentDetailsUnpaidBills,
            pathname: navigatorRoutesPaths.paymentDetailsUnpaidBills.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.Confirmation,
            pathname: navigatorRoutesPaths.confirmation.resolve(),
            query,
        },
    ] as const

    if (formValues.affiliateCode === AffiliateCode.OFFRE_EFFY) {
        return [
            {
                name: OnboardingRouteNames.MeterDetails,
                pathname: navigatorRoutesPaths.meterDetails.resolve(),
                query,
            },
            {
                name: OnboardingRouteNames.EnergyLandscape,
                pathname: navigatorRoutesPaths.effyEnergyLandscape.resolve(),
                query,
            },
            ...sharedSteps,
        ] satisfies WizardSteps<OnboardingRouteNames>
    }

    return [
        {
            name: OnboardingRouteNames.MeterDetails,
            pathname: navigatorRoutesPaths.meterDetails.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.ProviderCalendar,
            pathname: navigatorRoutesPaths.providerCalendar.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.EnergyLandscape,
            pathname: navigatorRoutesPaths.energyLandscape.resolve(),
            query,
        },
        ...sharedSteps,
    ] satisfies WizardSteps<OnboardingRouteNames>
}
